import { z } from 'zod';
import { appIdSchema } from './app.js';
import { expectedPriceFields } from './expected-price.js';
/**
 * The entitlement vocabulary — docs/004-domain-model.md:44-60 (Billing), argued in full in
 * `execution-plan/milestones/S7-billing-and-entitlements.md` Technical Notes.
 *
 * FEATURE KEYS ARE CODE. THE PLAN -> FEATURE MAPPING IS DATA. This looks like a contradiction
 * of CLAUDE.md's hardest-won lesson ("permissions and roles are code, not database rows") and
 * is not one: a wrong PERMISSION row is a security hole — someone sees data they must not, and
 * the damage is unbounded and often silent. A wrong PLAN mapping is a billing correction —
 * someone gets a feature they did not pay for, or does not get one they did, fixed by editing
 * one row and, at worst, issuing a refund. Permissions change when the product changes, which
 * is a deploy anyway; packaging changes when sales changes, which must not be. So the closed,
 * typed set of feature KEYS stays in code, in this one file, so a key cannot be typo'd and every
 * gate is greppable by its literal string — while the PLANS and their mapping to those keys are
 * rows, so pricing and packaging change with no deploy.
 *
 * THIS IS NOT `packages/authz`. That package answers "may this USER do this"; this file is
 * part of answering "has this TENANT paid for this". Folding the two together is exactly the
 * collapse `docs/004:52` forbids in as many words — one file, two gates, and the first refactor
 * that touches it merges them. `requireEntitlement()` (S7-TASK-005) never calls `can()`;
 * `requirePermission()` never reads a plan. The two gates must never merge.
 *
 * THIS IS NOT AN S8 FEATURE FLAG. A flag is temporary and engineering-owned (rollout, per-tenant
 * switching, kill switches); an entitlement is permanent and sales-owned (what a tenant bought).
 * They will look similar in the database and must not be unified later — S8 owns flags, this
 * file owns entitlements, and the distinction is written here so a later reader finds it before
 * deciding to merge the two tables.
 */
/**
 * Feature keys are CODE. The plan -> feature mapping is DATA. See the file header rules above.
 *
 * Format: `<app>.<feature>` — `app` is one of the five in PERMISSION_APPS
 * (platform | visitor | directory | survey | mailroom); `feature` is snake_case.
 * Deliberately NOT the three-segment permission format: a permission names an action on a
 * resource, an entitlement names a capability a tenant bought.
 *
 * ADDING A KEY IS ONE LINE HERE plus its shape, its availability, its label and a seed
 * migration (checklist: docs/019-billing-model.md "Adding a feature key"). A LIVE key that is
 * enforced nowhere is a lie to any customer who reads it on a plan comparison, so a live key and
 * its gate land in the SAME task. A key may be declared ahead of its feature only as `'planned'`
 * in `FEATURE_AVAILABILITY` — ungated, and never shown to tenants as included.
 */
export const FEATURE_KEYS = [
    /** Tenant-authored e-signature documents on check-in (S15). Gated by S7-TASK-005. */
    'visitor.agreements',
    /** Cap on how many Locations a tenant may hold. Gated by S7-TASK-006. */
    'platform.max_locations',
    /** Bulk CSV export of the admin visitor log. Gated by S7-TASK-012. NOT the evacuation
     *  roster — that is a safety surface and is never gated (milestone Technical Notes). */
    'visitor.log_export',
    /**
     * The PROFESSIONAL reports: hosts, dwell and occupancy, the pre-registration funnel, and
     * kiosk usage. Gated by S25 Phase 2 on all four `/api/web/analytics/*` routes.
     *
     * NOT the core reports. `/analytics/breakdown`, `/analytics/timeline` and
     * `/analytics/summary` stay ungated: the dashboard has shipped to every tenant since S16
     * and taking it away to sell it back is not packaging, it is a regression.
     */
    'visitor.reports_advanced',
    /**
     * The COMPLIANCE report: agreement acceptance coverage, photo-capture rate, mark-safe
     * history and the tenant's retention posture. Gated by S25 Phase 3 on
     * `GET /api/web/analytics/compliance`.
     *
     * NOT the evacuation roster and NOT its export. A safety surface is never sold (S14); this
     * key gates the after-the-fact report that links to the roster, never the roster itself.
     */
    'visitor.reports_compliance',
    /**
     * Cap on how many `User` rows may hold an ACTIVE `TenantMembership` in one tenant — seats,
     * in the sense every competitor prices on. A `'limit'`, like `platform.max_locations`, and
     * `null` means unlimited.
     *
     * ADDED WITH A PLAN AND NOT WITH ITS ENFORCEMENT, DELIBERATELY, AND THIS IS THE WHOLE ANSWER
     * TO GitHub issue #69. The free tier's "2 users" is a commercial term, and a term with no key
     * behind it cannot ever be applied — which is what #69 was filed about. The key, its shape,
     * its label and its per-plan numbers land here, with the plan they describe. The GATE lands
     * with the only route that can create a seat: `POST /api/web/members/invite`
     * (S26-TASK-017), as `assertWithinLimit(tenantId, 'platform.max_users', currentSeatCount)`,
     * copying `directory/service.ts:269`'s `platform.max_locations` call site verbatim.
     *
     * Visit volume has its own key, `visitor.monthly_visits` (owner decision 2026-10-24), and it is
     * SOFT by design: a visit is created on the device surface, and `lib/require-entitlement.ts:30-34`
     * forbids an entitlement gate there — a lapsed card must not strand a visitor in a lobby. So
     * that cap never refuses a check-in; past it, host notifications are skipped and admins see a
     * banner. See that key's comment. (S26-TASK-015 weighed the alternatives when no key existed.)
     */
    'platform.max_users',
    /**
     * Host-arrival texts a tenant may send per CALENDAR MONTH, UTC (PRD-005 NOTIF-13).
     *
     * THE FIRST KEY THAT CAPS A RATE RATHER THAN A POPULATION, and the difference decides three
     * things the other limit keys settle differently.
     *
     * (1) NO PLAN GETS `null`. `platform.max_locations` and `platform.max_users` are unlimited on
     * pro and enterprise because an extra row costs us nothing. A text costs real money per
     * message and pro has unlimited LOCATIONS, so an unlimited allowance on it is an unbounded
     * carrier bill on our highest-volume tier. Every plan carries a number; genuine exceptions
     * are per-tenant `Entitlement` overrides, which is what that table is for.
     *
     * (2) IT IS COUNTED, NOT MEASURED. The others answer "how many rows exist right now" with a
     * `count()` at write time. There is no row to count here — a sent text is an event — so the
     * meter is `SmsUsage`, a per-tenant-per-month counter the notification module reserves
     * against atomically. See `platform/notification/repository.ts#reserveSmsQuota`.
     *
     * (3) HITTING IT IS NOT A 402. `assertWithinLimit` throws `PLAN_LIMIT_REACHED`, which is the
     * right answer when a human clicked "add a location" and can act on it. This cap is reached
     * by a background side effect of a stranger checking in at a kiosk, so it degrades to a
     * SKIPPED delivery (`quota_exhausted`) and the check-in is untouched — the same reasoning
     * that keeps every gate off `/api/device/v1/*` (S7 Constraint 11).
     */
    'visitor.host_sms_monthly',
    /**
     * Cap on how many `VisitorBadgePrinter` rows may exist at ONE Location (PRD-009 PRT-01a).
     * Per-location, not per-tenant: a ten-site tenant on a one-printer plan gets ten printers,
     * one each, which is the shape the product actually has. Gated by S35-TASK-027 on
     * `POST /api/web/visitor/badge-printers`, via `billingService.assertWithinLimit` — the same
     * mechanism `platform.max_locations` uses, not a new one.
     */
    'visitor.max_printers_per_location',
    /**
     * Access to the Employee Directory app. No plan maps it (adr/0036 decision 3): it resolves
     * `{ enabled: false }` for every tenant until an operator overrides it on, which stays true
     * until Directory has a plan. Gated by S47-rsq5-TASK-014.
     */
    'directory.access',
    /** InstaCheckin Express, the employee mobile app (adr/0037 decision 4). Mapped by DATA onto every non-default PLAN-kind Visitor plan (migration 20261017180000); operator-overridable per tenant. Gates the Visitor module on /api/mobile/v1 (S48-lh45-TASK-008) and every /api/mobile/v1/visitor/* route (requireEntitlement). */
    'visitor.mobile_app',
    /** External directory sync (S49-3u50): connect Google Workspace (later Entra ID, SCIM) and keep Person current. A platform.* key because sync feeds the platform Person store both Visitor and Directory read, and so that /admin/plans can move it between any app's plans (milestone Technical Notes (b)). Mapped by DATA onto Visitor plans at Max and above (migration 20261020100000); operator-overridable per tenant. Gated by requireEntitlement on every /api/web/tenant/directory-sync route. */
    'platform.directory_sync',
    /** Company sign-in (S50-kqp9 Phase 4): claim the company's Google Workspace domain (later a Microsoft 365 tenant), prove it, test, and make it mandatory for everyone. A platform.* key so /admin/plans can move it between any app's plans (milestone Technical Notes (b)). Mapped by DATA onto Visitor plans at Max and above (migration 20261023135648_s50_enterprise_sso_entitlement); operator-overridable per tenant. Gated by requireEntitlement on the claim, test, verify and enforce routes; a lapse never turns enforcement off. */
    'platform.enterprise_sso',
    /** Cap on how many check-in notification recipients (people or addresses told on every arrival and checkout) may exist at ONE location, per location like `visitor.max_printers_per_location`. A `'limit'`; defaults to 10 on every PLAN-kind Visitor plan by DATA (migration 20261024000100), operator-overridable. Gated on POST /api/web/visitor/locations/:locationId/notify-recipients via `assertWithinLimit`. */
    'visitor.max_notify_recipients_per_location',
    /** Cap on employee contacts (`Person` rows) per tenant — the people a visitor can pick as a host and the Directory lists. A `'limit'`; `null` = unlimited. A `platform.*` key because Person is the platform store Visitor and Directory both read. Per-plan numbers are DATA (migration 20261024150000_plan_feature_keys). Counts ACTIVE EMPLOYEE/CONTRACTOR rows (`directory/repository.ts` `countPeopleForCap`). Gated by `assertWithinLimit` on createPerson and reactivation (402), the CSV import commit (402, all-or-nothing; the preview reports `peopleLimit`) and SCIM create/reactivate (402); a pull directory sync never refuses — it skips past the cap with a `PEOPLE_LIMIT_REACHED` warning. */
    'platform.max_people',
    /**
     * Check-ins per CALENDAR MONTH, UTC. A `'limit'`; `null` = unlimited. Per-plan numbers are DATA
     * (migration 20261024150000_plan_feature_keys).
     *
     * SOFT, BY DESIGN (owner decision 2026-10-24): it NEVER blocks a check-in. A visit is created on
     * the device surface, where `lib/require-entitlement.ts` forbids a gate — a lapsed card or a busy
     * month must not strand a visitor in a lobby. Past the cap, host notifications are skipped and
     * admins see a banner, the same degrade-don't-refuse shape `visitor.host_sms_monthly` takes.
     * LIVE — the soft gate lands in the next slice at the check-in notification fan-out and the
     * admin banner; never as `requireEntitlement` or a 402 on `/api/device/v1/*`.
     */
    'visitor.monthly_visits',
    /** Cap on paired kiosks per Location, per location like `visitor.max_printers_per_location`. A `'limit'`; `null` = unlimited. LIVE — the gate lands in the next slice at pairing-code mint (the admin route), never on the device surface. */
    'visitor.max_kiosks_per_location',
    /** The longest visit-history retention a tenant may set, in days. A `'limit'`; `null` = may keep forever. LIVE — the gate lands in the next slice on the retention-setting write. */
    'visitor.max_retention_days',
    /** Cap on `VisitorType` rows per tenant. A `'limit'`; `null` = unlimited. LIVE — the gate lands in the next slice on visitor-type create. */
    'visitor.max_visitor_types',
    /** Photo capture at check-in: adding or activating a PHOTO_CAPTURE flow screen. LIVE — the gate lands in the next slice on the flow-screen write; the kiosk itself is never gated. */
    'visitor.photo_capture',
    /** Pre-registration: creating invites, web and mobile. LIVE — the gate lands in the next slice on invite create (both surfaces). */
    'visitor.pre_registration',
    /** Custom kiosk branding: colors, welcome copy, per-location branding overrides (and the future welcome image/slideshow). The LOGO stays free on every plan and is never gated by this key. Gated by `requireEntitlement` on PUT /branding, PUT /locations/:locationId/branding and PUT .../branding/override; logo routes, reads and DELETE .../branding/override stay open. */
    'visitor.custom_branding',
    /** Slack and Microsoft Teams: install and delivery. LIVE — the gate lands in the next slice on the install routes and the chat delivery path. */
    'platform.chat_integrations',
    /** Hides "Powered by InstaCheckin" on the kiosk welcome screen. OFF ON EVERY PLAN BY DESIGN and never sold on a plan: it exists only so an operator can override it ON for one tenant (a big negotiated deal). LIVE — the read lands in the next slice where the kiosk welcome screen's config is built. */
    'visitor.remove_powered_by',
    /** PLANNED — a visitor watchlist. NOT BUILT: declared so packaging can be drafted; no gate until it ships (`FEATURE_AVAILABILITY`). */
    'visitor.watchlist',
    /** PLANNED — ID document scanning at check-in. NOT BUILT; no gate until it ships (`FEATURE_AVAILABILITY`). */
    'visitor.id_scan',
    /** PLANNED — public API keys and outbound webhooks. NOT BUILT; no gate until it ships (`FEATURE_AVAILABILITY`). */
    'platform.api_access',
    /** The tenant-facing audit log viewer (the console's Activity page). LIVE — gated on `GET /audit-events`. Every plan RECORDS events; this decides only who may read them back, so an upgrade unlocks the full history at once. */
    'platform.audit_log',
    /** Cap on DOCUMENT_SIGNATURE screens in one check-in flow, counted on the COMPOSED flow (its own screens plus what it inherits — what the kiosk shows). A `'limit'`; `null` = unlimited. 1 on every plan; an operator raises it per tenant as an override. LIVE — gated on adding a new signature screen (`addScreen`); a flow already past the cap keeps working and is never gated at publish or on the kiosk. */
    'visitor.max_signature_screens_per_flow',
    /** Cap on PHOTO_CAPTURE screens in one check-in flow, counted like `visitor.max_signature_screens_per_flow` (the composed flow). A `'limit'`; `null` = unlimited. 1 on every plan; an operator raises it per tenant as an override. Sits ON TOP of the boolean `visitor.photo_capture`, which still decides whether a plan may capture at all. LIVE — gated on adding a new photo screen (`addScreen`) only. */
    'visitor.max_photo_screens_per_flow',
    /** How many days back the Activity page reaches. A `'limit'`; `null` = unlimited. A READ window only: rows are never deleted for the plan, so a downgrade hides history and an upgrade shows it again. LIVE — clamps `from` on `GET /audit-events` and its export. */
    'platform.audit_log_days',
    /** CSV export of the Activity page. LIVE — gated on `GET /audit-events/export`. */
    'platform.audit_log_export',
];
/** Membership test set. Built once; a gate is set math, never a scan. */
export const FEATURE_KEY_SET = new Set(FEATURE_KEYS);
export function isFeatureKey(value) {
    return FEATURE_KEY_SET.has(value);
}
export const featureKeySchema = z.enum(FEATURE_KEYS);
/**
 * BOOLEAN vs LIMIT. A boolean feature is on or off and its `limit` is always `null`. A limit
 * feature is a numeric cap where `null` means UNLIMITED — never 0, never -1; the sentinel
 * ambiguity is exactly what a nullable column exists to avoid (the argument
 * `VisitorRetentionSetting.retentionDays` already makes in schema.prisma).
 */
export const FEATURE_SHAPES = ['boolean', 'limit'];
export const FEATURE_SHAPE = {
    'visitor.agreements': 'boolean',
    'platform.max_locations': 'limit',
    'visitor.log_export': 'boolean',
    'visitor.reports_advanced': 'boolean',
    'visitor.reports_compliance': 'boolean',
    'platform.max_users': 'limit',
    'visitor.host_sms_monthly': 'limit',
    'visitor.max_printers_per_location': 'limit',
    'directory.access': 'boolean',
    'visitor.mobile_app': 'boolean',
    'platform.directory_sync': 'boolean',
    'platform.enterprise_sso': 'boolean',
    'visitor.max_notify_recipients_per_location': 'limit',
    'platform.max_people': 'limit',
    'visitor.monthly_visits': 'limit',
    'visitor.max_kiosks_per_location': 'limit',
    'visitor.max_retention_days': 'limit',
    'visitor.max_visitor_types': 'limit',
    'visitor.photo_capture': 'boolean',
    'visitor.pre_registration': 'boolean',
    'visitor.custom_branding': 'boolean',
    'platform.chat_integrations': 'boolean',
    'visitor.remove_powered_by': 'boolean',
    'visitor.watchlist': 'boolean',
    'visitor.id_scan': 'boolean',
    'platform.api_access': 'boolean',
    'platform.audit_log': 'boolean',
    'visitor.max_signature_screens_per_flow': 'limit',
    'visitor.max_photo_screens_per_flow': 'limit',
    'platform.audit_log_days': 'limit',
    'platform.audit_log_export': 'boolean',
};
/**
 * IS THE FEATURE BUILT? `'live'` = it exists and its key is enforced (or, for the keys listed in
 * the API's `PENDING_GATE_KEYS`, about to be). `'planned'` = the key is declared so plans and
 * packaging can be drafted on /admin/plans, but THE FEATURE IS NOT BUILT. A planned key:
 *   - must NOT be gated anywhere (an API test fails if its literal reaches `requireEntitlement`,
 *     `assertWithinLimit` or `getEntitlement`) — there is nothing to gate;
 *   - must NOT be shown to tenants as an included feature (a price list omits it), because
 *     selling what does not exist is the lie the FEATURE_KEYS comment warns about;
 *   - is shown to operators with a "Not built yet" badge.
 * Flip a key to `'live'` in the SAME commit as its gate.
 */
export const FEATURE_AVAILABILITIES = ['live', 'planned'];
export const featureAvailabilitySchema = z.enum(FEATURE_AVAILABILITIES);
export const FEATURE_AVAILABILITY = {
    'visitor.agreements': 'live',
    'platform.max_locations': 'live',
    'visitor.log_export': 'live',
    'visitor.reports_advanced': 'live',
    'visitor.reports_compliance': 'live',
    'platform.max_users': 'live',
    'visitor.host_sms_monthly': 'live',
    'visitor.max_printers_per_location': 'live',
    'directory.access': 'live',
    'visitor.mobile_app': 'live',
    'platform.directory_sync': 'live',
    'platform.enterprise_sso': 'live',
    'visitor.max_notify_recipients_per_location': 'live',
    'platform.max_people': 'live',
    'visitor.monthly_visits': 'live',
    'visitor.max_kiosks_per_location': 'live',
    'visitor.max_retention_days': 'live',
    'visitor.max_visitor_types': 'live',
    'visitor.photo_capture': 'live',
    'visitor.pre_registration': 'live',
    'visitor.custom_branding': 'live',
    'platform.chat_integrations': 'live',
    'visitor.remove_powered_by': 'live',
    'visitor.watchlist': 'planned',
    'visitor.id_scan': 'planned',
    'platform.api_access': 'planned',
    'platform.audit_log': 'live',
    'visitor.max_signature_screens_per_flow': 'live',
    'visitor.max_photo_screens_per_flow': 'live',
    'platform.audit_log_days': 'live',
    'platform.audit_log_export': 'live',
};
/**
 * Keys a TENANT never sees on a plan comparison — enforced, and set by operators on /admin/plans and
 * per tenant, but not something we advertise (owner, 2026-10-28: the per-flow screen caps stay quiet
 * for now). Unlike a planned key the feature is built and gated; it is only unlisted. Remove a key
 * here to start showing it. `visitor.mobile_app` is unlisted until Express ships, and
 * `visitor.remove_powered_by` because the attribution is marketing and turning it off is an operator
 * decision per tenant, never something a plan sells (owner, 2026-10-31).
 */
export const UNLISTED_FEATURE_KEYS = new Set([
    'visitor.max_signature_screens_per_flow',
    'visitor.max_photo_screens_per_flow',
    'visitor.mobile_app',
    'visitor.remove_powered_by',
]);
/** True when a tenant-facing plan comparison must leave the key out. See `UNLISTED_FEATURE_KEYS`. */
export function isUnlistedFeature(key) {
    return UNLISTED_FEATURE_KEYS.has(key);
}
/** True when the key is declared but its feature is not built. See `FEATURE_AVAILABILITY`. */
export function isPlannedFeature(key) {
    return FEATURE_AVAILABILITY[key] === 'planned';
}
/**
 * A plan's stable code. A VALIDATED STRING, NOT A UNION — a closed union would make adding a
 * plan a deploy, and the whole point of keeping the catalog in rows is that packaging changes
 * without one. Refines docs/004:48 ("code is stable and referenced in code"); the milestone's
 * Technical Notes carry the argument.
 */
export const planCodeSchema = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/);
/**
 * Ours, not a vendor's. `PAST_DUE` STILL GRANTS ENTITLEMENTS — a provider's dunning retries
 * for days and cutting a customer off on the first failed card is hostile and off-market.
 * `CANCELED` grants nothing and the tenant falls back to the default plan.
 */
export const SUBSCRIPTION_STATUSES = ['ACTIVE', 'TRIALING', 'PAST_DUE', 'CANCELED'];
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
/** Which layer decided an entitlement. Computed on read; never stored. */
export const ENTITLEMENT_SOURCES = ['DEFAULT', 'PLAN', 'ADDON', 'OVERRIDE'];
export const entitlementSourceSchema = z.enum(ENTITLEMENT_SOURCES);
/**
 * One resolved answer. `limit` is always `null` for a `boolean`-shaped key.
 *
 * `shape` given `'boolean'` with a non-null `limit` is ACCEPTED by this schema and normalised
 * to `null` by the producer (S7-TASK-003's DTO), not refused here: this is a wire contract, and
 * a `superRefine` coupling the two fields would make a server one release ahead unable to add a
 * third shape. Do not "tighten" this later.
 */
export const entitlementDTOSchema = z.object({
    featureKey: featureKeySchema,
    shape: z.enum(FEATURE_SHAPES),
    enabled: z.boolean(),
    limit: z.number().int().min(0).nullable(),
    source: entitlementSourceSchema,
});
/**
 * ONE APPLICATION'S PLAN STATE for one tenant (S38). Before S38 this was a single tenant-wide
 * triple on `tenantEntitlementsDTOSchema`, which could not express a tenant paying for two
 * applications on independent cycles — the whole reason S38 exists.
 *
 * An entry exists only for an application the tenant holds (or held) a plan for, so
 * `subscriptionStatus` is the status of that subscription. The type stays nullable for older
 * fixtures; the API no longer sends `null` here.
 */
export const appPlanStateDTOSchema = z.object({
    app: appIdSchema,
    planCode: planCodeSchema,
    planName: z.string().min(1),
    subscriptionStatus: subscriptionStatusSchema.nullable(),
    currentPeriodEnd: z.string().datetime().nullable(),
    /**
     * Handoff 0010 §1–§2. The interval this application's line is billed at by the billing
     * provider — `null` when the application is not provider-backed (a trial, a free plan, a
     * sales-led assignment), which is also how a client tells "changes plan in the app" from
     * "goes through checkout". OPTIONAL on the wire only so an older fixture stays valid; the
     * tenant-facing `GET /billing/entitlements` always sends it.
     */
    billedInterval: z.lazy(() => planIntervalSchema).nullable().optional(),
    /**
     * Handoff 0010 §2. A plan change the owner asked for that takes effect at the end of the
     * current period (a downgrade, annual → monthly, or down to Free). `null` when none is
     * pending. Same optionality note as `billedInterval`.
     */
    pendingChange: z.lazy(() => pendingPlanChangeDTOSchema).nullable().optional(),
    /**
     * True when the owner cancelled this application and it is set to end at `currentPeriodEnd`:
     * access continues until then. Same optionality note as `billedInterval`.
     */
    cancelAtPeriodEnd: z.boolean().optional(),
});
/**
 * The whole commercial answer for one tenant.
 *
 * `entitlements` is COMPLETE — one entry per FEATURE_KEYS member, always, so a client never has
 * to distinguish "absent" from "off".
 *
 * `apps` is SPARSE, and deliberately so: an application the tenant holds no plan for is
 * ABSENT, which has exactly one meaning. (The completeness argument for `entitlements` does not
 * apply — "no plan for Survey" and "Survey plan that grants nothing" are different facts and a
 * missing entry states the first unambiguously.) Ordered by `APP_IDS`, and a client may rely on
 * the order. There is NO `platform` entry: no plan is sold for the suite itself, and the account
 * limits (`platform.max_locations`, `platform.max_users`) are in `entitlements`, resolved as the
 * most generous value across the tenant's application plans.
 */
export const tenantEntitlementsDTOSchema = z.object({
    apps: z.array(appPlanStateDTOSchema),
    entitlements: z.array(entitlementDTOSchema),
    /**
     * True iff the tenant has a billing account at the billing provider (a `BillingCustomer`
     * row) — the precondition of the hosted billing portal. A tenant on its signup trial or on
     * Free that never went through checkout has none, and `POST /billing/portal` would refuse
     * it, so a client hides "Manage payment" rather than offering a control that cannot work.
     *
     * Defaults to `false` when absent, so a browser bundle that is newer than the API it talks
     * to mid-deploy still parses the response — and errs towards hiding the control.
     */
    hasBillingAccount: z.boolean().default(false),
});
/** In lock-step with the `PlanInterval` enum in schema.prisma — order included; asserted by
 *  packages/db/src/billing-enums.test.ts. */
export const PLAN_INTERVALS = ['MONTH', 'YEAR'];
export const planIntervalSchema = z.enum(PLAN_INTERVALS);
/** What a pending plan change does when it takes effect (handoff 0010 §2): move to another
 *  self-serve plan or interval, or end the paid Visitor plan and fall back to Visitor Free. */
export const PENDING_PLAN_CHANGE_KINDS = ['plan', 'free'];
/**
 * A plan change scheduled for the end of the current period (handoff 0010 §2) — what the Billing
 * page shows as "Changes to Visitor Core on Oct 29, 2026 — Cancel change". `planCode`/`planName`
 * name the plan the application moves TO (Visitor Free for `kind: 'free'`); `interval` is the
 * interval it will be billed at, `null` for `free` (nothing is billed).
 */
export const pendingPlanChangeDTOSchema = z
    .object({
    kind: z.enum(PENDING_PLAN_CHANGE_KINDS),
    planCode: planCodeSchema,
    planName: z.string().min(1),
    interval: planIntervalSchema.nullable(),
    effectiveAt: z.string().datetime(),
})
    .strict();
/** One plan's answer for one feature key. Deliberately NOT `entitlementDTOSchema`: that
 *  carries `source`, which is a property of a RESOLUTION and meaningless for a catalog row. */
export const planFeatureDTOSchema = z.object({
    featureKey: featureKeySchema,
    shape: z.enum(FEATURE_SHAPES),
    enabled: z.boolean(),
    limit: z.number().int().min(0).nullable(),
    /** `FEATURE_AVAILABILITY[featureKey]`. OPTIONAL on the wire so a client one release ahead of
     *  the API still parses; a reader treats absent as `'live'`. */
    availability: featureAvailabilitySchema.optional(),
});
/** What a price is charged per (S38 Phase 4). In lock-step with Prisma's `BillingDimension`
 *  (packages/db/src/billing-enums.test.ts). */
export const BILLING_DIMENSIONS = ['LOCATION', 'SEAT', 'FLAT', 'METERED'];
export const billingDimensionSchema = z.enum(BILLING_DIMENSIONS);
/** One active price of a plan (S38-TASK-062). Amount, interval and what it is charged per.
 *  Never an id or a provider ref (S7). */
export const planPriceDTOSchema = z.object({
    interval: planIntervalSchema,
    priceMinorUnits: z.number().int().min(0),
    currency: z.string().regex(/^[A-Z]{3}$/),
    dimension: billingDimensionSchema,
});
/** One row of the price list. `features` is COMPLETE — one entry per FEATURE_KEYS member,
 *  always — so a comparison table can never disagree with the gate. `id` and
 *  `externalPriceRef` are deliberately absent (S7: a DTO is an allow-list, and a vendor's
 *  opaque price id is never a client's business). */
export const planSummaryDTOSchema = z.object({
    code: planCodeSchema,
    name: z.string().min(1),
    /** Which application this plan sells (S38). Never `platform` — no plan is sold for the
     *  suite itself. */
    app: appIdSchema,
    priceMinorUnits: z.number().int().min(0),
    currency: z.string().regex(/^[A-Z]{3}$/),
    interval: planIntervalSchema,
    /** What the REPORTED price (MONTH wins) is charged per (S38 Phase 4/5). */
    dimension: billingDimensionSchema,
    /** The plan's own trial length in days, or null when the plan is never trialled — the
     *  length a second app's trial runs (S38-TASK-053/-054); the signup trial is a separate,
     *  fixed 14 days. */
    trialDays: z.number().int().min(0).nullable(),
    /** Every ACTIVE price of the plan, MONTH before YEAR — what an interval toggle chooses
     *  between. Never empty. The top-level `priceMinorUnits`/`interval`/`dimension` remain the
     *  REPORTED price (MONTH wins) for readers that show one. */
    prices: z.array(planPriceDTOSchema).min(1),
    /** Sold through self-serve checkout. `false` = shown for comparison, sold by sales
     *  ("Contact sales"); checkout refuses it (S38-TASK-063). */
    selfServe: z.boolean(),
    isDefault: z.boolean(),
    sortOrder: z.number().int(),
    features: z.array(planFeatureDTOSchema),
});
/** One row of the OPERATOR catalog (`GET /api/web/billing/catalog`, S38-TASK-070): a price-list
 *  row plus whether customers may buy it in this deployment. `launched: false` = the plan's
 *  application is not sold yet (docs/019 "Which apps are sold"); an operator may still put a
 *  tenant on it. */
export const catalogPlanDTOSchema = planSummaryDTOSchema.extend({ launched: z.boolean() });
/**
 * The `details` body of a 402. Read it with the parser, never by indexing — the same rule
 * `parseValidationFailureDetails` states in ./http.js, and for the same reason: this is wire
 * data from a server that may be a release ahead.
 */
export const planUpgradeRequiredDetailsSchema = z.object({
    featureKey: featureKeySchema,
    currentPlanCode: planCodeSchema,
});
export function parsePlanUpgradeRequiredDetails(details) {
    const parsed = planUpgradeRequiredDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
/**
 * The `details` body of a product app's `PLAN_SELECTION_REQUIRED` 402 (S38-TASK-050). The
 * `platform` instance of the wall carries no `details` at all — this shape exists only for the
 * per-app wall a product router mounts on itself.
 */
export const planSelectionRequiredDetailsSchema = z.object({ app: appIdSchema });
export function parsePlanSelectionRequiredDetails(details) {
    const parsed = planSelectionRequiredDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
export const planLimitReachedDetailsSchema = z.object({
    featureKey: featureKeySchema,
    currentPlanCode: planCodeSchema,
    limit: z.number().int().min(0),
    current: z.number().int().min(0),
});
export function parsePlanLimitReachedDetails(details) {
    const parsed = planLimitReachedDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
/**
 * Body of `POST /api/web/billing/checkout-session`. The only input is WHICH PLAN and, since
 * S38-TASK-063, WHICH INTERVAL — the quantity is the tenant's location count and is computed
 * server-side, and the return URLs are built from server configuration. A client that could
 * name its own return URL would be an open redirect with a payment page in front of it.
 */
export const checkoutSessionRequestSchema = z.object({
    planCode: planCodeSchema,
    /** Which of the plan's prices to bill (S38-TASK-063). Omitted = monthly, the default the
     *  owner chose on 2026-09-26. */
    interval: planIntervalSchema.optional(),
    /** Handoff 0010 §5 row 1: the amounts the owner was shown for `priceChangeOfCheckout(…)` —
     *  price × active count per interval, before we redirect to the provider's page. */
    ...expectedPriceFields,
});
/** Body of `POST /api/web/billing/apps` — a tenant owner adding an application (S38-TASK-053).
 *  `.strict()`: an extra field is a client bug, not silently ignored input. */
export const addAppRequestSchema = z.object({ planCode: planCodeSchema, ...expectedPriceFields }).strict();
/** Body of `PUT /api/web/billing/tenants/:tenantId/apps/:app/trial` — an operator granting a
 *  tenant a plan's trial (S38-TASK-053). `.strict()` for the same reason as above. */
export const appTrialGrantRequestSchema = z.object({ planCode: planCodeSchema, ...expectedPriceFields }).strict();
/** Body of `PUT /api/web/billing/tenants/:tenantId/apps/:app/plan` — an operator assigning a
 *  tenant a SALES-LED plan (S38-TASK-067). `.strict()`, as the trial-grant body. */
export const appPlanAssignRequestSchema = z.object({ planCode: planCodeSchema, ...expectedPriceFields }).strict();
/**
 * The answer to both `POST /billing/checkout-session` and `POST /billing/portal-session`:
 * an absolute URL the browser is sent to. It carries NO provider identifier — a session id or
 * a customer ref is a third-party internal id and never reaches a client (docs/008 S7).
 */
export const hostedSessionDTOSchema = z.object({
    url: z.string().url(),
});
/**
 * The body of `PATCH /api/web/billing/plans/:planCode/features/:featureKey`.
 *
 * Both fields are required, so a partial write cannot silently leave a stale
 * limit behind a flipped boolean. `limitValue: null` means UNLIMITED — the same
 * meaning it carries on `PlanEntitlement` and in resolution — and is the only
 * legal value for a `BOOLEAN`-shaped key; the service rejects a number there.
 */
export const planFeatureUpdateSchema = z
    .object({
    enabled: z.boolean(),
    limitValue: z.number().int().min(0).max(1_000_000).nullable(),
})
    .strict();
/** One `Entitlement` override row as an operator sees it. Dates are ISO 8601 strings. */
export const tenantOverrideDTOSchema = z
    .object({
    featureKey: featureKeySchema,
    enabled: z.boolean(),
    limitValue: z.number().int().min(0).nullable(),
    note: z.string().nullable(),
    expiresAt: z.string().datetime().nullable(),
    /**
     * True when `expiresAt` is in the past. Computed on the server so a client
     * clock cannot disagree with the resolver about whether a deal is live.
     * A lapsed override is IGNORED by resolution and is NEVER deleted
     * (Phase 4 sketch) — the row stays so the screen can say when it lapsed.
     */
    lapsed: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
/** The body of `PUT /api/web/billing/tenants/:tenantId/overrides/:featureKey`. */
export const tenantOverrideUpsertSchema = z
    .object({
    enabled: z.boolean(),
    limitValue: z.number().int().min(0).max(1_000_000).nullable(),
    /** Why this tenant has a deal the price list does not describe. Required — an
     *  unexplained bespoke grant is the thing nobody can audit a year later. */
    note: z.string().trim().min(1).max(500),
    expiresAt: z.string().datetime().nullable(),
})
    .strict();
/** The whole payload of `GET /api/web/billing/tenants/:tenantId/entitlements`. */
export const tenantEntitlementAdminDTOSchema = z
    .object({
    tenantId: z.string().min(1),
    tenantName: z.string().min(1),
    tenantSlug: z.string().min(1),
    resolved: tenantEntitlementsDTOSchema,
    overrides: z.array(tenantOverrideDTOSchema),
})
    .strict();
export const BILLING_PAYER_KINDS = ['CUSTOMER', 'PARTNER'];
/**
 * `GET /api/web/billing/payer` — who pays for this tenant, and whether THIS principal may change
 * billing. Deliberately carries no rate, amount, discount or provider reference: a customer whose
 * partner pays must never learn the partner's price from us.
 */
export const billingPayerDTOSchema = z
    .object({
    payer: z.enum(BILLING_PAYER_KINDS),
    /** The paying partner's name; null when the tenant pays for itself. */
    partnerName: z.string().min(1).nullable(),
    /** True for the tenant's own billing manager when it pays for itself, and for the paying partner's setup session; false otherwise. */
    canManage: z.boolean(),
})
    .strict();
//# sourceMappingURL=entitlement.js.map